/**
 * Snijplan (cutting plan) voor ondertapijten op maat.
 *
 * Spiegelt de automatische berekening van de Kleda field-app (UnderlayPage),
 * en voegt parsing/serialisatie toe voor de handmatige override die kantoor
 * vanuit de office-app op het ondertapijt-WI zet (veld Ondertapijt_Snijplan__c).
 *
 * Override-formaat: komma-gescheiden rolbreedtes in cm, bv. "240" (1 stuk) of
 * "130,113" (2 stukken). Leeg/null = automatisch.
 *
 * De snijlengte blijft áltijd afgeleid uit de kleedmaten (langste zijde − 10);
 * de override bepaalt alleen het aantal stukken + de rolbreedte(s).
 */

// Beschikbare rolbreedtes in cm (60–240 in stappen van 10) — gelijk aan field-app.
export const ROLL_WIDTHS = [
  60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 160, 170, 180, 190, 200, 210, 220, 230, 240,
] as const;

export const MAX_ROLL_WIDTH = 240;

// Marge tussen kleed en ondertapijt: 5cm per kant = 10cm totaal, zodat het
// ondertapijt makkelijk ONDER het kleed te plaatsen is.
// Webshop-orders (Werk Order Fulfillment_Type__c = 'Eigen verzending') worden op
// de EXACT bestelde maat gemaakt — daar is geen kleed om onder te leggen, marge = 0.
// Gespiegeld aan de field-app UnderlayPage.
export const UNDERLAY_MARGIN_CM = 10;
export const WEBSHOP_MARGIN_CM = 0;

/** De marge die voor dit fulfillment-type geldt: webshop exact, kleden 10cm. */
export function marginForFulfillment(fulfillmentType: string | null | undefined): number {
  return fulfillmentType === 'Eigen verzending' ? WEBSHOP_MARGIN_CM : UNDERLAY_MARGIN_CM;
}

export interface CutPlan {
  pieces: number;
  rolls: number[]; // rolbreedtes, aflopend gesorteerd
  cutLength: number; // snijlengte per rol
  underlayLength: number; // totale lengte ondertapijt
  underlayWidth: number; // totale breedte ondertapijt (= som rollen)
  isManual: boolean; // true als afkomstig van een handmatige override
}

/** Rond af naar de dichtstbijzijnde geldige rolbreedte (10cm-stap, geklemd 60–240). */
export function clampRoll(value: number): number {
  const rounded = Math.ceil(value / 10) * 10;
  if (rounded < 60) return 60;
  if (rounded > MAX_ROLL_WIDTH) return MAX_ROLL_WIDTH;
  return rounded;
}

/** Afgeleide snijlengte (langste kleedzijde − marge), of diameter − marge bij rond. */
export function afgeleideSnijlengte(
  lengthCm: number | null,
  widthCm: number | null,
  isRound: boolean,
  diameterCm: number | null,
  margin: number = UNDERLAY_MARGIN_CM
): number | null {
  if (isRound && diameterCm) return Math.max(0, diameterCm - margin);
  if (!lengthCm || !widthCm) return null;
  return Math.max(lengthCm, widthCm) - margin;
}

/**
 * Automatische berekening — identiek aan de field-app:
 * - rond → 1 stuk (diameter − 10)
 * - breedte ≤ 240 → 1 stuk
 * - breedte > 240 → 2 stukken die exact optellen
 */
export function calculateAutoCutPlan(
  lengthCm: number | null,
  widthCm: number | null,
  isRound: boolean,
  diameterCm: number | null,
  margin: number = UNDERLAY_MARGIN_CM
): CutPlan | null {
  if (isRound && diameterCm) {
    const d = Math.max(0, diameterCm - margin);
    const roll = Math.min(MAX_ROLL_WIDTH, Math.max(60, Math.ceil(d / 10) * 10));
    return { pieces: 1, rolls: [roll], cutLength: d, underlayLength: d, underlayWidth: d, isManual: false };
  }

  if (!lengthCm || !widthCm) return null;

  const underlayLength = Math.max(lengthCm, widthCm) - margin;
  const underlayWidth = Math.min(lengthCm, widthCm) - margin;

  if (underlayWidth <= MAX_ROLL_WIDTH) {
    const roll = Math.max(60, Math.ceil(underlayWidth / 10) * 10);
    return { pieces: 1, rolls: [roll], cutLength: underlayLength, underlayLength, underlayWidth, isManual: false };
  }

  const half = underlayWidth / 2;
  const roll1 = Math.ceil(half / 10) * 10;
  const roll2 = underlayWidth - roll1;
  const rolls = [roll1, roll2].sort((a, b) => b - a);

  return { pieces: 2, rolls, cutLength: underlayLength, underlayLength, underlayWidth, isManual: false };
}

/** Parse een override-string naar een lijst rolbreedtes (aflopend), of null. */
export function parseSnijplanOverride(raw: string | null | undefined): number[] | null {
  if (!raw) return null;
  const rolls = raw
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (rolls.length === 0) return null;
  return rolls.sort((a, b) => b - a);
}

/** Serialiseer rolbreedtes naar het opslagformaat (aflopend, komma-gescheiden). */
export function formatSnijplanOverride(rolls: number[]): string {
  return [...rolls].sort((a, b) => b - a).join(',');
}

/** Bouw een CutPlan uit expliciete rolbreedtes; snijlengte afgeleid uit kleedmaten. */
export function cutPlanFromRolls(
  rolls: number[],
  lengthCm: number | null,
  widthCm: number | null,
  isRound: boolean,
  diameterCm: number | null,
  margin: number = UNDERLAY_MARGIN_CM
): CutPlan | null {
  if (rolls.length === 0) return null;
  const snij = afgeleideSnijlengte(lengthCm, widthCm, isRound, diameterCm, margin);
  if (snij == null) return null;
  const sorted = [...rolls].sort((a, b) => b - a);
  const underlayWidth = sorted.reduce((sum, r) => sum + r, 0);
  return {
    pieces: sorted.length,
    rolls: sorted,
    cutLength: snij,
    underlayLength: snij,
    underlayWidth,
    isManual: true,
  };
}

/** Standaard rolbreedte(s) voor een gegeven aantal stukken, als startpunt in de editor. */
export function defaultRolls(pieces: number, underlayWidth: number): number[] {
  if (pieces <= 1) return [clampRoll(underlayWidth)];
  const half = underlayWidth / 2;
  return [clampRoll(Math.ceil(half)), clampRoll(Math.floor(half))].sort((a, b) => b - a);
}
